import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentTypesLogicService } from '@app/logic/document-types';
import { DocumentGroupMappedItem } from '@app/logic/documents';
import { TagsLogicService } from '@app/logic/tags';
import { DOCUMENT_REQUIRED_TYPE_ENUM, DOCUMENT_APPROVAL_TYPE_ENUM, IDocumentTypeDto, ITagDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { filter, find, findIndex, orderBy, some } from 'lodash';
import { IDocumentTypeDocumentGroupDto } from '@classictechsolutions/hubapi-transpiled-enums/build/main/lib/dtos/DocumentTypeDocumentGroupDto';
import { IDocumentTypeDocumentGroupApprovalTagDto } from '@classictechsolutions/hubapi-transpiled-enums/build/main/lib/dtos/DocumentTypeDocumentGroupApprovalTagDto';
import { take } from 'rxjs';

interface IData {
    documentGroup: DocumentGroupMappedItem;
    documentType: IDocumentTypeDocumentGroupDto;
}

@Component({
    selector: 'cb-document-group-type-dialog',
    templateUrl: './document-group-type-dialog.component.html',
    styleUrls: ['./document-group-type-dialog.component.scss'],
    standalone: false
})
export class DocumentGroupTypeDialogComponent {
    public static readonly MIN_WIDTH = '65%';
    public documentTypes: IDocumentTypeDto[];
    public requiredTypes = DOCUMENT_REQUIRED_TYPE_ENUM.toLookup();
    public approvalTypes = DOCUMENT_APPROVAL_TYPE_ENUM.toLookup();
    public tags: ITagDto[] = [];
    public availableTags: ITagDto[];
    public selectedTagId: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
        public readonly dialogRef: MatDialogRef<DocumentGroupTypeDialogComponent>,
        protected readonly documentTypeLogicService: DocumentTypesLogicService,
        protected readonly tagsLogicService: TagsLogicService,
    ) {
        this.documentTypeLogicService.$getList().pipe(take(1)).subscribe(documentTypes => this.documentTypes = documentTypes as IDocumentTypeDto[]);
        this.tagsLogicService.$getList().pipe(take(1)).subscribe(tags => {
            this.tags = tags;
            this.refreshAvailableTags();
        });
        this.data.documentType.approvalTags = this.data.documentType.approvalTags ?? [];
    }

    public querySearch = (searchText: string): IDocumentTypeDto[] => {
        return filter(this.documentTypes, (documentType: IDocumentTypeDto) => {
            const isExisitng = some(this.data.documentGroup.types, { documentTypeId: documentType.id });
            return !isExisitng && documentType.label.toLowerCase().includes(searchText.toLowerCase());
        });
    };

    public canSave(): boolean {
        return !!this.data.documentType?.documentType?.id;
    }

    public onSaveClicked(): void {
        if (this.canSave()) {
            this.data.documentType.documentTypeId = this.data.documentType.documentType.id;
            this.dialogRef.close(this.data.documentType);
        }
    }

    public onAddTagCliced(): void {
        if (this.selectedTagId) {
            const found = some(this.data.documentType.approvalTags, { userTagId: this.selectedTagId });
            if (!found) {
                this.data.documentType.approvalTags.push({ userTagId: this.selectedTagId } as IDocumentTypeDocumentGroupApprovalTagDto);
                this.selectedTagId = null;
                this.refreshAvailableTags();
            }
        }
    }

    public onRemoveTagClick(deletedTag: IDocumentTypeDocumentGroupApprovalTagDto): void {
        const indexToRemove = findIndex(this.data.documentType.approvalTags, { userTagId: deletedTag.userTagId });
        if (indexToRemove >= 0) {
            this.data.documentType.approvalTags.splice(indexToRemove, 1);
            this.refreshAvailableTags();
        }
    }

    public getTagDisplayLabel(tag: IDocumentTypeDocumentGroupApprovalTagDto): string {
        const found = find(this.tags, { id: tag.userTagId });
        return found ? found.name : '';
    }

    private refreshAvailableTags(): void {
        this.availableTags = orderBy(
            filter(this.tags, tag => {
                return findIndex(this.data.documentType.approvalTags, itemTag => {
                    return tag.id === itemTag.userTagId;
                }) < 0;
            }), 'name');
    }
}
